import { useState } from "react";
import AccountLayout from "@/components/feature/AccountLayout";
import { mockWishlist } from "@/mocks/wishlist";
import { useCart } from "@/hooks/useCart";
import type { WishlistItem } from "@/mocks/wishlist";

export default function WishlistPage() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language?.startsWith("ar");
  const { addItem } = useCart();
  const [wishlistItems, setWishlistItems] = useState<WishlistItem[]>(mockWishlist);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const handleRemove = (productId: string) => {
    setRemovingId(productId);
    setTimeout(() => {
      setWishlistItems((prev) => prev.filter((item) => item.productId !== productId));
      setRemovingId(null);
    }, 300);
  };

  const handleAddToCart = (item: WishlistItem) => {
    addItem({
      productId: item.productId,
      nameEn: item.nameEn,
      nameAr: item.nameAr,
      slug: item.slug,
      price: item.price,
      imageUrl: item.imageUrl,
      quantity: 1,
      maxStock: 99,
      selectedVariants: {},
    });
  };

  return (
    <AccountLayout activeTab="wishlist">
      {wishlistItems.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
          {wishlistItems.map((item) => {
            const isRemoving = removingId === item.productId;
            const discount = item.compareAtPrice
              ? Math.round(((item.compareAtPrice - item.price) / item.compareAtPrice) * 100)
              : 0;

            return (
              <div
                key={item.productId}
                className={`bg-background-50 border border-background-200 rounded-xl overflow-hidden transition-all duration-300 ${
                  isRemoving ? "opacity-0 scale-95" : ""
                }`}
              >
                {/* Image */}
                <Link
                  to={`/product/${item.slug}`}
                  className="block relative aspect-square bg-background-100 overflow-hidden cursor-pointer"
                >
                  <img
                    src={item.imageUrl}
                    alt={isAr ? item.nameAr : item.nameEn}
                    className="w-full h-full object-cover object-top"
                  />
                  {item.compareAtPrice && (
                    <span className="absolute top-3 left-3 px-2.5 py-1 bg-primary-500 text-white text-xs font-semibold rounded-md">
                      -{discount}%
                    </span>
                  )}
                  {/* Remove button */}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleRemove(item.productId);
                    }}
                    className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 hover:bg-white text-foreground-600 hover:text-foreground-900 flex items-center justify-center transition-all cursor-pointer"
                    aria-label={t("account.remove_from_wishlist")}
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </Link>

                {/* Info */}
                <div className="p-4">
                  <Link
                    to={`/product/${item.slug}`}
                    className="text-sm font-medium text-foreground-900 hover:text-primary-600 transition-colors line-clamp-2 leading-snug cursor-pointer mb-2"
                  >
                    {isAr ? item.nameAr : item.nameEn}
                  </Link>

                  {/* Rating */}
                  <div className="flex items-center gap-1.5 mb-3">
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <i
                          key={i}
                          className={`text-xs ${
                            i < Math.floor(item.rating)
                              ? "ri-star-fill text-accent-500"
                              : "ri-star-line text-foreground-300"
                          }`}
                        ></i>
                      ))}
                    </div>
                    <span className="text-xs text-foreground-500">({item.reviewCount})</span>
                  </div>

                  {/* Price & Add to cart */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-base font-semibold text-foreground-950">
                        ${item.price.toFixed(2)}
                      </span>
                      {item.compareAtPrice && (
                        <span className="text-sm text-foreground-400 line-through">
                          ${item.compareAtPrice.toFixed(2)}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => handleAddToCart(item)}
                      disabled={!item.inStock}
                      className={`px-4 py-2 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                        item.inStock
                          ? "bg-primary-500 text-white hover:bg-primary-600"
                          : "bg-foreground-100 text-foreground-400 cursor-not-allowed"
                      }`}
                    >
                      {item.inStock ? t("featured.add_to_cart") : t("product.out_of_stock")}
                    </button>
                  </div>

                  <p className="text-xs text-foreground-400 mt-3">
                    {t("account.added")}{" "}
                    {new Date(item.addedAt).toLocaleDateString(
                      isAr ? "ar-EG" : "en-US",
                      { month: "short", day: "numeric" }
                    )}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-20">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
            <i className="ri-heart-line text-2xl text-foreground-400"></i>
          </div>
          <h3 className="text-lg font-medium text-foreground-900 mb-2">{t("account.empty_wishlist")}</h3>
          <p className="text-sm text-foreground-500 mb-6 max-w-sm mx-auto">{t("account.empty_wishlist_desc")}</p>
          <Link
            to="/products"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white font-semibold rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap text-sm"
          >
            {t("account.browse_products")}
          </Link>
        </div>
      )}
    </AccountLayout>
  );
}